import { isSupabaseConfigured, requireSupabaseClient } from '../lib/supabaseClient';

const RUNTIME_DATABASE_CONFIG_KEY = 'fms:database-config';

function hasRuntimeSupabaseConfig() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return false;
    const raw = window.localStorage.getItem(RUNTIME_DATABASE_CONFIG_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    const url = parsed?.supabase?.url || '';
    const anonKey = parsed?.supabase?.anonKey || '';
    return parsed?.provider === 'supabase' && Boolean(url && anonKey);
  } catch {
    return false;
  }
}

const TX_TABLE = 'transactions';
const AGENT_TABLE = 'agents';
const VENTURE_TABLE = 'ventures';
const ACCOUNT_TABLE = 'accounts';
const LOAN_TABLE = 'loans';

export const isSupabaseSyncEnabled =
  (process.env.REACT_APP_SUPABASE_SYNC_ENABLED === 'true' || hasRuntimeSupabaseConfig()) && isSupabaseConfigured;

const parseJsonSafe = (value, fallback) => {
  if (value == null) return fallback;
  if (typeof value !== 'string') return fallback;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

export function toRemoteTransaction(tx) {
  return {
    id: tx.id,
    type: tx.type,
    venture_id: tx.ventureId || null,
    branch_id: tx.branchId || null,
    date: tx.date,
    description: tx.description || '',
    reference: tx.reference || null,
    amount: Number(tx.amount || 0),
    category_account_id: tx.categoryAccountId || null,
    balance_account_id: tx.balanceAccountId || null,
    payment_method: tx.paymentMethod || null,
    agent_id: tx.agentId || null,
    loan_id: tx.loanId || null,
    commission: Number(tx.commission || 0),
    pos_type: tx.posType || null,
    bundles: Number(tx.bundles || 0),
    duration: tx.duration || null,
    plan: tx.plan || null,
    customer_name: tx.customerName || null,
    customer_phone: tx.customerPhone || null,
    note: tx.note || null,
    created_at: tx.createdAt || null,
    updated_at: tx.updatedAt || tx.createdAt || null,
    deleted_at: tx.deletedAt || null,
  };
}

export function fromRemoteTransaction(row) {
  return {
    id: row.id,
    type: row.type,
    ventureId: row.venture_id || '',
    branchId: row.branch_id || '',
    date: row.date,
    description: row.description || '',
    reference: row.reference || '',
    amount: Number(row.amount || 0),
    categoryAccountId: row.category_account_id || '',
    balanceAccountId: row.balance_account_id || '',
    paymentMethod: row.payment_method || '',
    agentId: row.agent_id || '',
    loanId: row.loan_id || '',
    commission: Number(row.commission || 0),
    posType: row.pos_type || '',
    bundles: Number(row.bundles || 0),
    duration: row.duration || '',
    plan: row.plan || '',
    customerName: row.customer_name || '',
    customerPhone: row.customer_phone || '',
    note: row.note || '',
    createdAt: row.created_at || new Date().toISOString(),
    updatedAt: row.updated_at || row.created_at || new Date().toISOString(),
    deletedAt: row.deleted_at || '',
  };
}

export function toRemoteAgent(agent) {
  return {
    id: agent.id,
    name: agent.name || '',
    phone: agent.phone || null,
    role: agent.role || null,
    venture_id: agent.ventureId || null,
    branch_id: agent.branchId || null,
    created_at: agent.createdAt || null,
    updated_at: agent.updatedAt || agent.createdAt || null,
    deleted_at: agent.deletedAt || null,
  };
}

export function fromRemoteAgent(row) {
  return {
    id: row.id,
    name: row.name || '',
    phone: row.phone || '',
    role: row.role || '',
    ventureId: row.venture_id || '',
    branchId: row.branch_id || '',
    commission: 0,
    createdAt: row.created_at || new Date().toISOString(),
    updatedAt: row.updated_at || row.created_at || new Date().toISOString(),
    deletedAt: row.deleted_at || '',
  };
}

export function toRemoteVenture(venture) {
  return {
    id: venture.id,
    name: venture.name || '',
    color: venture.color || null,
    description: venture.description || null,
    branches: JSON.stringify(Array.isArray(venture.branches) ? venture.branches : []),
    created_at: venture.createdAt || null,
    updated_at: venture.updatedAt || venture.createdAt || null,
    deleted_at: venture.deletedAt || null,
  };
}

export function fromRemoteVenture(row) {
  return {
    id: row.id,
    name: row.name || '',
    color: row.color || '#c9a84c',
    description: row.description || '',
    branches: parseJsonSafe(row.branches, []),
    createdAt: row.created_at || new Date().toISOString(),
    updatedAt: row.updated_at || row.created_at || new Date().toISOString(),
    deletedAt: row.deleted_at || '',
  };
}

export function toRemoteAccount(account) {
  return {
    id: account.id,
    code: account.code || '',
    name: account.name || '',
    type: account.type || 'asset',
    normal: account.normal || null,
    created_at: account.createdAt || null,
    updated_at: account.updatedAt || account.createdAt || null,
    deleted_at: account.deletedAt || null,
  };
}

export function fromRemoteAccount(row) {
  return {
    id: row.id,
    code: row.code || '',
    name: row.name || '',
    type: row.type || 'asset',
    normal: row.normal || '',
    createdAt: row.created_at || new Date().toISOString(),
    updatedAt: row.updated_at || row.created_at || new Date().toISOString(),
    deletedAt: row.deleted_at || '',
  };
}

export function toRemoteLoan(loan) {
  return {
    id: loan.id,
    name: loan.name || '',
    lender: loan.lender || null,
    principal: Number(loan.principal || 0),
    venture_id: loan.ventureId || null,
    start_date: loan.startDate || null,
    due_date: loan.dueDate || null,
    created_at: loan.createdAt || null,
    updated_at: loan.updatedAt || loan.createdAt || null,
    deleted_at: loan.deletedAt || null,
  };
}

export function fromRemoteLoan(row) {
  return {
    id: row.id,
    name: row.name || '',
    lender: row.lender || '',
    principal: Number(row.principal || 0),
    ventureId: row.venture_id || '',
    startDate: row.start_date || '',
    dueDate: row.due_date || '',
    createdAt: row.created_at || new Date().toISOString(),
    updatedAt: row.updated_at || row.created_at || new Date().toISOString(),
    deletedAt: row.deleted_at || '',
  };
}

export async function upsertTransactions(rows) {
  if (!isSupabaseSyncEnabled || !rows?.length) return { data: [], error: null };
  const client = requireSupabaseClient();
  return client.from(TX_TABLE).upsert(rows, { onConflict: 'id' }).select();
}

export async function fetchTransactionsSince(updatedAtIso) {
  if (!isSupabaseSyncEnabled) return { data: [], error: null };
  const client = requireSupabaseClient();
  const query = client
    .from(TX_TABLE)
    .select('*')
    .order('updated_at', { ascending: true });

  if (updatedAtIso) {
    query.gt('updated_at', updatedAtIso);
  }

  return query;
}

export async function fetchAllTransactions() {
  if (!isSupabaseSyncEnabled) return { data: [], error: null };
  const client = requireSupabaseClient();
  return client.from(TX_TABLE).select('*').order('updated_at', { ascending: true });
}

export async function upsertAgents(rows) {
  if (!isSupabaseSyncEnabled || !rows?.length) return { data: [], error: null };
  const client = requireSupabaseClient();
  return client.from(AGENT_TABLE).upsert(rows, { onConflict: 'id' }).select();
}

export async function fetchAllAgents() {
  if (!isSupabaseSyncEnabled) return { data: [], error: null };
  const client = requireSupabaseClient();
  return client.from(AGENT_TABLE).select('*').order('updated_at', { ascending: true });
}

export async function upsertVentures(rows) {
  if (!isSupabaseSyncEnabled || !rows?.length) return { data: [], error: null };
  const client = requireSupabaseClient();
  return client.from(VENTURE_TABLE).upsert(rows, { onConflict: 'id' }).select();
}

export async function fetchAllVentures() {
  if (!isSupabaseSyncEnabled) return { data: [], error: null };
  const client = requireSupabaseClient();
  return client.from(VENTURE_TABLE).select('*').order('updated_at', { ascending: true });
}

export async function upsertAccounts(rows) {
  if (!isSupabaseSyncEnabled || !rows?.length) return { data: [], error: null };
  const client = requireSupabaseClient();
  return client.from(ACCOUNT_TABLE).upsert(rows, { onConflict: 'id' }).select();
}

export async function fetchAllAccounts() {
  if (!isSupabaseSyncEnabled) return { data: [], error: null };
  const client = requireSupabaseClient();
  return client.from(ACCOUNT_TABLE).select('*').order('updated_at', { ascending: true });
}

export async function upsertLoans(rows) {
  if (!isSupabaseSyncEnabled || !rows?.length) return { data: [], error: null };
  const client = requireSupabaseClient();
  return client.from(LOAN_TABLE).upsert(rows, { onConflict: 'id' }).select();
}

export async function fetchAllLoans() {
  if (!isSupabaseSyncEnabled) return { data: [], error: null };
  const client = requireSupabaseClient();
  return client.from(LOAN_TABLE).select('*').order('updated_at', { ascending: true });
}
